// Domain detail-page parser. Every Domain listing page, whichever provider
// fetched it (Bright Data, Zyte, Firecrawl), embeds the full listing as
// Next.js page data:
//   <script id="__NEXT_DATA__" type="application/json">
//   props.pageProps.componentProps.rootGraphQuery.listingByIdV2
// That object is Domain's own structured record for THIS listing: sold date,
// exact sold price, bed/bath/car counts, property type enum, land area,
// geolocation and agency. Read it instead of the rendered text so raw-HTML
// providers (no LLM extraction) still yield a complete snapshot.
//
// Never read componentProps.listingsMap: it holds OTHER nearby listings.
// When the page URL ends in a Domain listing id, listingByIdV2 must carry
// the same id or the page is not the one we asked for and nothing is parsed.

import { type DiscoveryPreview, type ExtractedAsset, extractGenericAssets } from "@repo/adapter-core";

interface DomainMedia {
  url?: string;
  type?: string;
  mediaCategory?: string;
}

interface DomainListing {
  listingId?: number | string;
  status?: string;
  listingMode?: string;
  bedrooms?: number | null;
  bathrooms?: number | null;
  carspaces?: number | null;
  propertyTypes?: string[] | null;
  landAreaSqm?: number | null;
  buildingArea?: number | null;
  soldDetails?: {
    soldDate?: { isoDate?: string | null } | null;
    soldPrice?: { rawValues?: { exactPrice?: number | null } | null } | null;
  } | null;
  priceDetails?: { displayPrice?: string | null } | null;
  displayableAddress?: {
    unitNumber?: string | null;
    streetNumber?: string | null;
    street?: string | null;
    suburbName?: string | null;
    postcode?: string | null;
    state?: string | null;
    geolocation?: { latitude?: number | null; longitude?: number | null } | null;
  } | null;
  agency?: { name?: string | null } | null;
  largeMedia?: DomainMedia[] | null;
}

interface DomainPage {
  listing: DomainListing;
  summaryPropertyType: string | null;
}

// Domain propertyTypes enum -> the canonical labels the dashboard groups by
// (CANONICAL_PROPERTY_TYPES in apps/web/src/lib/api/propertyDatabase.ts).
// Unmapped enums fall back to Domain's own display label for the listing.
const PROPERTY_TYPE_LABELS: Readonly<Record<string, string>> = {
  HOUSE: "House",
  NEW_HOUSE_LAND: "House",
  APARTMENT_UNIT_FLAT: "Apartment",
  NEW_APARTMENTS: "Apartment",
  PENTHOUSE: "Penthouse",
  STUDIO: "Studio",
  TOWNHOUSE: "Townhouse",
  TERRACE: "Townhouse",
  VILLA: "Villa",
  SEMI_DETACHED: "Semi-Detached",
  DUPLEX: "Semi-Detached",
  BLOCK_OF_UNITS: "Block of Units",
  VACANT_LAND: "Land",
  NEW_LAND: "Land",
  DEVELOPMENT_SITE: "Land",
  ACREAGE_SEMI_RURAL: "Rural",
  RURAL: "Rural",
  FARM: "Rural",
  RETIREMENT: "Retirement",
  CAR_SPACE: "Other",
};

const LISTING_MODE_TYPES: Readonly<Record<string, string>> = {
  SOLD: "sold",
  BUY: "buy",
  RENT: "rent",
};

const NEXT_DATA_RE = /<script[^>]*\bid=["']__NEXT_DATA__["'][^>]*>([\s\S]*?)<\/script>/i;
const URL_LISTING_ID_RE = /-(\d{7,})\/?(?:[?#].*)?$/;
const PHOTO_CAP = 5;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readDomainPage(html: string, url: string): DomainPage | null {
  const match = html.match(NEXT_DATA_RE);
  if (!match) return null;
  let data: unknown;
  try {
    data = JSON.parse(match[1]);
  } catch {
    return null;
  }
  const componentProps = (data as { props?: { pageProps?: { componentProps?: unknown } } })
    ?.props?.pageProps?.componentProps;
  if (!isRecord(componentProps)) return null;
  const listing = (componentProps.rootGraphQuery as { listingByIdV2?: unknown } | undefined)
    ?.listingByIdV2;
  if (!isRecord(listing)) return null;

  const urlListingId = url.match(URL_LISTING_ID_RE)?.[1];
  if (urlListingId && String(listing.listingId) !== urlListingId) return null;

  const summary = componentProps.listingSummary;
  const summaryPropertyType = isRecord(summary) && typeof summary.propertyType === "string"
    ? summary.propertyType.trim() || null
    : null;
  return { listing: listing as DomainListing, summaryPropertyType };
}

function count(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 50 ? value : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

// Below this a hidden price behind "Price Withheld" is treated as a placeholder, not a sale.
const WITHHELD_HIDDEN_PRICE_FLOOR = 150_000;

/** "$1,465,000" → 1465000; anything else (ranges, "Price Withheld", "Contact agent") → null. */
function displayedAmount(display: string | null | undefined): number | null {
  if (!display || !/^\$\s?[\d,]+$/.test(display.trim())) return null;
  const value = Number(display.replace(/[^\d]/g, ""));
  return Number.isFinite(value) && value >= 1000 ? value : null;
}

export function parseDomainDetailHtml(html: string, url: string): Record<string, unknown> {
  const page = readDomainPage(html, url);
  if (!page) return {};
  const { listing } = page;
  const hit: Record<string, unknown> = {};

  const address = listing.displayableAddress;
  if (address) {
    const street = [text(address.streetNumber), text(address.street)].filter(Boolean).join(" ");
    const unit = text(address.unitNumber);
    const suburb = text(address.suburbName);
    const state = text(address.state)?.toUpperCase() ?? null;
    const postcode = text(address.postcode);
    if (street) {
      const locality = [suburb, state, postcode].filter(Boolean).join(" ");
      hit.address = [unit ? `${unit}/${street}` : street, locality].filter(Boolean).join(", ");
    }
    if (suburb) hit.suburb = suburb;
    if (state) hit.state = state;
    if (postcode) hit.postcode = postcode;
    const latitude = address.geolocation?.latitude;
    const longitude = address.geolocation?.longitude;
    if (typeof latitude === "number" && typeof longitude === "number"
      && Number.isFinite(latitude) && Number.isFinite(longitude)) {
      hit.latitude = latitude;
      hit.longitude = longitude;
    }
  }

  const mode = text(listing.listingMode)?.toUpperCase();
  const isSold = text(listing.status)?.toUpperCase() === "SOLD" || mode === "SOLD";
  const listingType = isSold ? "sold" : mode ? LISTING_MODE_TYPES[mode] : undefined;
  if (listingType) hit.listingType = listingType;

  if (isSold) {
    const soldDate = text(listing.soldDetails?.soldDate?.isoDate)?.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (soldDate) hit.saleDate = soldDate;
    // Explicit null when Domain has no sold amount: the page shows none, so
    // a provider's guide price or guessed figure must not survive the merge.
    // The hidden exactPrice can be junk (30000 behind "$5,400,000", 1100 behind "$623,000";
    // 74 of 7,635 disagreed, 2026-09-26), so a plain displayed figure wins. When the price is
    // withheld the hidden figure is usually right (20/21 within 2% of SQM) but placeholders like
    // 30000 or 1000 appear, so it must clear a floor. Both outcomes are recorded for audit.
    const exactPrice = listing.soldDetails?.soldPrice?.rawValues?.exactPrice;
    const hidden = typeof exactPrice === "number" && Number.isFinite(exactPrice) ? exactPrice : null;
    const displayed = displayedAmount(text(listing.priceDetails?.displayPrice));
    let salePrice: number | null = null;
    if (displayed !== null) {
      salePrice = displayed;
      hit.priceSource = "display";
      if (hidden !== null && hidden !== displayed) hit.hiddenPriceMismatch = hidden;
    } else if (hidden !== null && hidden >= WITHHELD_HIDDEN_PRICE_FLOOR) {
      salePrice = hidden;
      hit.priceSource = "hidden";
    } else if (hidden !== null) {
      hit.hiddenPriceRejected = hidden;
    }
    hit.salePrice = salePrice;
    hit.price = salePrice;
  }
  const priceDisplay = text(listing.priceDetails?.displayPrice);
  if (priceDisplay) hit.priceDisplay = priceDisplay;

  const bedrooms = count(listing.bedrooms);
  if (bedrooms !== null) hit.bedrooms = bedrooms;
  const bathrooms = count(listing.bathrooms);
  if (bathrooms !== null) hit.bathrooms = bathrooms;
  const parking = count(listing.carspaces);
  if (parking !== null) hit.parking = parking;

  const typeEnum = listing.propertyTypes?.find((t) => typeof t === "string" && t.trim());
  const propertyType = (typeEnum && PROPERTY_TYPE_LABELS[typeEnum.toUpperCase()]) ?? page.summaryPropertyType;
  if (propertyType) hit.propertyType = propertyType;

  if (typeof listing.landAreaSqm === "number" && listing.landAreaSqm > 0) {
    hit.landSize = String(listing.landAreaSqm);
    hit.landSizeUnit = "sqm";
  }
  if (typeof listing.buildingArea === "number" && listing.buildingArea > 0) {
    hit.propertySize = String(listing.buildingArea);
    hit.propertySizeUnit = "sqm";
  }

  const agencyName = text(listing.agency?.name);
  if (agencyName) hit.agencyName = agencyName;

  return hit;
}

/**
 * Photos and floorplans from listingByIdV2.largeMedia (Domain's largest
 * rendition, 1920x1080 fit-in). Same listing-id guard as the parser: a
 * mismatched page yields no assets rather than a neighbour's photos. A page
 * without Next.js data falls back to the generic extractor.
 */
export function extractDomainAssets(html: string, baseUrl: string): ExtractedAsset[] {
  if (!NEXT_DATA_RE.test(html)) return extractGenericAssets(html, baseUrl);
  const page = readDomainPage(html, baseUrl);
  if (!page) return [];
  const photos: ExtractedAsset[] = [];
  const floorplans: ExtractedAsset[] = [];
  const seen = new Set<string>();
  for (const media of page.listing.largeMedia ?? []) {
    const sourceUrl = text(media?.url);
    if (!sourceUrl || !/^https:\/\//i.test(sourceUrl) || seen.has(sourceUrl)) continue;
    if (media.mediaCategory && media.mediaCategory !== "IMAGE") continue;
    seen.add(sourceUrl);
    if (media.type === "floorplan") {
      floorplans.push({ type: "floorplan", sourceUrl });
    } else if (media.type === "photo" && photos.length < PHOTO_CAP) {
      photos.push({ type: "photo", sourceUrl, indexInListing: photos.length });
    }
  }
  return [...photos, ...floorplans];
}

// Search-results pages carry one card per listing in componentProps.listingsMap, ordered by
// listingSearchResultIds. A sold card's tag reads "Sold at auction 15 Aug 2026" (or "Sold by
// private treaty …", "Sold prior to auction …"): that date lets discovery drop a listing outside
// the run's window before paying for its detail page, and stop paginating past the window start.
// Domain's own filter only offers soldwithin=365, so without it every search ran a year deep.
const CARD_MONTHS: Readonly<Record<string, string>> = {
  jan: "01",
  feb: "02",
  mar: "03",
  apr: "04",
  may: "05",
  jun: "06",
  jul: "07",
  aug: "08",
  sep: "09",
  oct: "10",
  nov: "11",
  dec: "12",
};
const CARD_DATE_RE =
  /\b(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{4})\b/i;

/** "Sold at auction 15 Aug 2026" → "2026-08-15"; null when the tag is not a dated sale. */
export function domainCardSoldDate(tagText: string | null | undefined): string | null {
  if (!tagText || !/\bsold\b/i.test(tagText)) return null;
  const match = tagText.match(CARD_DATE_RE);
  if (!match) return null;
  return `${match[3]}-${CARD_MONTHS[match[2].toLowerCase()]}-${match[1].padStart(2, "0")}`;
}

/** Cards on a Domain search page; null when the page is not one (no listingsMap). */
export function parseDomainDiscoveryPreviews(
  html: string,
  pageUrl: string,
): DiscoveryPreview[] | null {
  const match = html.match(NEXT_DATA_RE);
  if (!match) return null;
  let data: unknown;
  try {
    data = JSON.parse(match[1]);
  } catch {
    return null;
  }
  const componentProps = (data as { props?: { pageProps?: { componentProps?: unknown } } })?.props
    ?.pageProps?.componentProps;
  if (!isRecord(componentProps) || !isRecord(componentProps.listingsMap)) return null;
  const listingsMap = componentProps.listingsMap;
  const ids = Array.isArray(componentProps.listingSearchResultIds)
    ? componentProps.listingSearchResultIds.map(String)
    : Object.keys(listingsMap);

  const cards: DiscoveryPreview[] = [];
  for (const id of ids) {
    const model = (listingsMap[id] as { listingModel?: unknown } | undefined)?.listingModel;
    if (!isRecord(model)) continue;
    const path = text(model.url);
    if (!path) continue;
    let url: string;
    try {
      url = new URL(path, pageUrl).href;
    } catch {
      continue;
    }
    const card: DiscoveryPreview = { url };
    const saleDate = domainCardSoldDate(
      text((model.tags as { tagText?: unknown } | undefined)?.tagText),
    );
    if (saleDate) card.saleDate = saleDate;
    const price = text(model.price);
    if (price) card.priceDisplay = price;
    const address = isRecord(model.address) ? model.address : null;
    if (address) {
      const suburb = text(address.suburb);
      const state = text(address.state)?.toUpperCase();
      const postcode = text(address.postcode);
      const street = text(address.street);
      if (suburb) card.suburb = suburb;
      if (state) card.state = state;
      if (postcode) card.postcode = postcode;
      if (street)
        card.address = [street, [suburb, state, postcode].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", ");
    }
    const features = isRecord(model.features) ? model.features : null;
    const propertyType = features
      ? (text(features.propertyTypeFormatted) ?? text(features.propertyType))
      : null;
    if (propertyType) card.propertyType = propertyType;
    // For-sale cards (follow-until-sold option B) are recorded without a detail fetch, so keep
    // everything the card shows.
    const entry = listingsMap[id] as { id?: unknown } | undefined;
    const listingId = entry?.id ?? /-(\d+)$/.exec(path)?.[1];
    if (listingId !== undefined && listingId !== null) card.listingId = String(listingId);
    const street = address ? text(address.street) : null;
    if (street) card.street = street;
    const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
    const fields = {
      bedrooms: num(features?.beds),
      bathrooms: num(features?.baths),
      parking: num(features?.parking),
      landSize:
        features && text(features.landUnit) === "m²" && (num(features.landSize) ?? 0) > 0
          ? num(features.landSize)
          : undefined,
      latitude: num(address?.lat),
      longitude: num(address?.lng),
      auctionAt: text(model.auction) ?? undefined,
    };
    for (const [k, v] of Object.entries(fields)) {
      if (v !== undefined) (card as unknown as Record<string, unknown>)[k] = v;
    }
    cards.push(card);
  }
  return cards;
}
